import { AuthProvider, useAuth } from 'react-oidc-context';
import { useEffect, useRef, type ReactNode } from 'react';
import { WebStorageStateStore } from 'oidc-client-ts';
import { configureApiClient } from '../lib/api/client';

/**
 * Authentication against the shared Keycloak realm.
 *
 * <p>The panel talks to Keycloak directly with Authorization Code + PKCE and holds the resulting
 * access token; the ad server only validates it. No password ever reaches this application or its
 * backend, which is the entire reason for using the identity provider the VPS already runs.
 */

/**
 * Read from the environment so a developer can point at a local Keycloak without editing code, and
 * so the production values are set at deploy time rather than compiled in.
 */
const oidcConfig = {
  authority:
    import.meta.env.VITE_KEYCLOAK_AUTHORITY ??
    'https://auth.elbloqueganador.com/realms/dramyeri',
  client_id: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'ad-server-panel',
  redirect_uri: `${window.location.origin}/`,
  post_logout_redirect_uri: `${window.location.origin}/`,
  response_type: 'code',
  /**
   * The audience the ad server checks. Without this scope the token is valid for the realm but not
   * for this application, and every admin call would return 403 with nothing to explain why.
   */
  scope: 'openid profile email',
  // Tokens in session storage rather than local storage: closing the tab ends the session, which
  // is the right default for an operations tool on a possibly shared machine.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  // Renew silently before expiry, so a long editing session does not fail mid-save.
  automaticSilentRenew: true,
  onSigninCallback: () => {
    // Strip the authorization code from the address bar; leaving it there means a refresh replays
    // a code that has already been consumed.
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

export function AdServerAuthProvider({ children }: { children: ReactNode }) {
  return <AuthProvider {...oidcConfig}>{children}</AuthProvider>;
}

/**
 * Bridges the auth state into the API client.
 *
 * <p>The client needs a token per request and needs to know what to do on a 401. Wiring it here
 * rather than importing auth into the client keeps the client free of the OIDC library.
 */
export function ApiClientBridge({ children }: { children: ReactNode }) {
  const auth = useAuth();

  /**
   * The live auth object, read through a ref.
   *
   * <p>The callbacks below must see current state without the effect depending on
   * it. Depending on `auth` directly would re-register them on every render, and
   * the re-registration is what turned a single expired token into a redirect
   * loop.
   */
  const authRef = useRef(auth);
  authRef.current = auth;

  /** A 401 storm must trigger at most one redirect, not one per failed request. */
  const redirecting = useRef(false);

  useEffect(() => {
    configureApiClient({
      getToken: () => authRef.current.user?.access_token,
      onUnauthorized: () => {
        if (redirecting.current) {
          return;
        }
        redirecting.current = true;
        // The token is gone or rejected. Signing in again is the only useful next step.
        void authRef.current.signinRedirect();
      },
    });
    // Registered once. The callbacks read current state through the ref.
  }, []);

  return <>{children}</>;
}

/**
 * Gate that renders children only for an authenticated operator.
 *
 * <p>Redirects to Keycloak rather than showing a local login form, because there is no local
 * password to collect.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuth();

  /**
   * Guards against redirecting more than once.
   *
   * <p>An earlier version depended on the whole `auth` object, which the library
   * returns as a new value on every render. The effect therefore re-ran
   * constantly: it redirected to Keycloak, Keycloak returned the user
   * authenticated, React re-rendered, and the effect redirected again — a loop
   * the user experiences as the panel "entering and leaving" repeatedly.
   *
   * <p>A ref rather than state, because flipping state would itself cause a
   * render and defeat the purpose.
   */
  const redirecting = useRef(false);
  const authRef = useRef(auth);
  authRef.current = auth;

  useEffect(() => {
    const settled = !auth.isLoading && !auth.activeNavigator;
    const needsLogin = settled && !auth.isAuthenticated && !auth.error;

    if (needsLogin && !redirecting.current) {
      redirecting.current = true;
      void authRef.current.signinRedirect();
    }

    // Cleared once authenticated, so a later expiry can redirect again.
    if (auth.isAuthenticated) {
      redirecting.current = false;
    }
    // The individual flags, never the object: the object is new on every render,
    // the flags are not. Depending on the object is what caused the loop.
  }, [auth.isLoading, auth.activeNavigator, auth.isAuthenticated, auth.error]);

  if (auth.isLoading || auth.activeNavigator) {
    return <AuthMessage title="Verificando sesión" detail="Un momento." />;
  }

  if (auth.error) {
    return (
      <AuthMessage
        title="No se pudo iniciar sesión"
        detail={auth.error.message}
        action={
          <button
            onClick={() => void auth.signinRedirect()}
            className="rounded bg-electric-blue px-4 py-2 text-body-md font-medium text-text-primary"
          >
            Reintentar
          </button>
        }
      />
    );
  }

  if (!auth.isAuthenticated) {
    return <AuthMessage title="Redirigiendo al inicio de sesión" detail="Un momento." />;
  }

  return <>{children}</>;
}

function AuthMessage({
  title,
  detail,
  action,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-surface-primary px-6 text-center">
      <h1 className="text-headline-md text-text-primary">{title}</h1>
      <p className="max-w-md text-body-md text-text-secondary">{detail}</p>
      {action}
    </div>
  );
}
